
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';

interface PremiumSignupFormProps {
  assessmentSlug: string;
  onSuccess: () => void;
  onSwitchToLogin: () => void;
}

function extractTeamFromEmail(email: string): string {
  try {
    const domain = email.split('@')[1];
    if (!domain) return '';
    const parts = domain.split('.');
    // Use the first part of the domain as team name (e.g., gapstars from gapstars.net)
    return parts[0] || '';
  } catch {
    return '';
  }
}

export default function PremiumSignupForm({ assessmentSlug, onSuccess, onSwitchToLogin }: PremiumSignupFormProps) {
  const [email, setEmail] = useState('');
  const [team, setTeam] = useState('');
  const [teamAutoDetected, setTeamAutoDetected] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value.includes('@')) {
      const detected = extractTeamFromEmail(value);
      if (detected && detected.length > 1) {
        setTeam(detected.charAt(0).toUpperCase() + detected.slice(1));
        setTeamAutoDetected(true);
      } else {
        setTeam('');
        setTeamAutoDetected(false);
      }
    } else {
      if (teamAutoDetected) {
        setTeam('');
        setTeamAutoDetected(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      // Sign up the user
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/assessment/${assessmentSlug}/premium`,
          data: {
            team,
            is_premium_user: true,
          },
        },
      });

      if (signUpError) {
        if (signUpError.message.includes('already registered')) {
          setError('An account with this email already exists. Please login instead.');
        } else {
          setError(signUpError.message);
        }
        setLoading(false);
        return;
      }

      // If user was created, find or create organization and add as member
      if (data.user) {
        const orgName = team || 'Unknown';
        
        // Try to find existing organization by name
        let organizationId: string | null = null;
        const { data: existingOrg } = await supabase
          .from('organizations')
          .select('id')
          .ilike('name', orgName)
          .maybeSingle();

        if (existingOrg) {
          organizationId = existingOrg.id;
        } else {
          // Create new organization
          const { data: newOrg, error: orgError } = await supabase
            .from('organizations')
            .insert({
              name: orgName,
              domain: email.split('@')[1] || null,
              created_by: data.user.id,
            })
            .select('id')
            .single();

          if (!orgError && newOrg) {
            organizationId = newOrg.id;
          } else {
            console.error('Failed to create organization:', orgError);
          }
        }

        // Add user as organization member
        if (organizationId) {
          const { error: memberError } = await supabase
            .from('organization_members')
            .insert({
              id: data.user.id,
              email,
              full_name: null,
              organization_id: organizationId,
              role: 'viewer',
              user_type: 'team_member',
              invitation_status: 'accepted',
              is_active: true,
            });

          if (memberError && !memberError.message.includes('duplicate')) {
            console.error('Failed to create organization member record:', memberError);
          }
        }
      }

      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-4">
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            A confirmation email has been sent to <strong>{email}</strong>. 
            Please check your inbox and click the link to verify your account, then you can log in.
          </AlertDescription>
        </Alert>
        <Button onClick={onSwitchToLogin} className="w-full">
          Go to Login
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="signup-email">Email</Label>
        <Input
          id="signup-email"
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => handleEmailChange(e.target.value)}
          required
          disabled={loading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-team">
          Team / Organization
        </Label>
        <Input
          id="signup-team"
          type="text"
          placeholder="Your team or organization"
          value={team}
          onChange={(e) => {
            setTeam(e.target.value);
            setTeamAutoDetected(false);
          }}
          disabled={loading}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-password">Password</Label>
        <div className="relative">
          <Input
            id="signup-password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Must be at least 8 characters
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="signup-confirm">Confirm Password</Label>
        <div className="relative">
          <Input
            id="signup-confirm"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={loading}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <Button type="submit" className="w-full" disabled={loading}>
        {loading ? 'Creating Account...' : 'Create Account'}
      </Button>

      <p className="text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <button type="button" onClick={onSwitchToLogin} className="text-primary hover:underline font-medium">
          Login
        </button>
      </p>
    </form>
  );
}
